import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { History, Plus, X, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import {
  getAiConversation,
  getAiConversations,
  getAiSuggestedQuestions,
  sendAiChatMessage,
  sendAiFeedback,
} from "../../services/aiAssistantService";
import RobotAvatar from "./RobotAvatar";
import AIVisualGuide from "./AIVisualGuide";
import { normalizeVisualGuide } from "../../utils/aiVisualGuide";

const MAX_SUGGESTIONS = 6;

const unwrap = (data) => data?.Result ?? data?.result ?? data;

const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  return (
    data?.Message ||
    data?.message ||
    data?.Error ||
    data?.error ||
    err?.message ||
    fallback
  );
};

const normalizeLanguage = (value) => {
  const code = String(value || "").toLowerCase().slice(0, 2);
  return code === "de" ? "de" : "en";
};

const getInitialLanguage = () => {
  const stored =
    localStorage.getItem("languageCode") ||
    localStorage.getItem("uiLanguage") ||
    localStorage.getItem("language");
  return normalizeLanguage(stored || "en");
};

const getContextFromPath = (pathname) => {
  const path = pathname.toLowerCase();
  const parts = path.split("/").filter(Boolean);

  if (path === "/dashboard" || path === "/") {
    return { screenName: "Dashboard", eventId: null };
  }

  if (path === "/events") {
    return { screenName: "Events", eventId: null };
  }

  if (parts[0] === "events" && parts[1] && parts[2] === "gallery") {
    return { screenName: "Albums", eventId: Number(parts[1]) || parts[1] };
  }

  if (parts[0] === "events" && parts[1]) {
    return { screenName: "EventDetails", eventId: Number(parts[1]) || parts[1] };
  }

  if (parts[0] === "gallery" || parts[0] === "upload" || parts[0] === "uploads") {
    return { screenName: "Galleries", eventId: null };
  }

  if (parts[0] === "rooms" && parts[2] === "gallery") {
    return { screenName: "Albums", eventId: null };
  }

  if (parts[0] === "rooms") {
    return { screenName: "Assistants", eventId: null };
  }

  if (parts[0] === "auto-upload") {
    return { screenName: "Galleries", eventId: null };
  }

  if (parts[0] === "companion") {
    return { screenName: "Assistants", eventId: null };
  }

  if (parts[0] === "wallet") {
    return { screenName: "Wallet", eventId: null };
  }

  if (parts[0] === "settings") {
    return { screenName: "Settings", eventId: null };
  }

  if (parts[0] === "qr") {
    return { screenName: "QR", eventId: null };
  }

  return { screenName: "PromoterPanel", eventId: null };
};

const normalizeSuggestion = (item) => {
  if (typeof item === "string") return item;
  return item?.Question || item?.question || item?.Text || item?.text || item?.Title || item?.title || "";
};

const getConversationTitle = (item, t) =>
  item?.Title ||
  item?.title ||
  item?.LastMessage ||
  item?.lastMessage ||
  item?.Message ||
  item?.message ||
  t("history.conversationFallback", { id: item?.Id || item?.id || "" }).trim();

const getConversationId = (item) =>
  item?.Id ?? item?.id ?? item?.ConversationId ?? item?.conversationId;

// PiA's backend replies with lightweight Markdown (mainly **bold**). We don't
// pull in a full Markdown library for this one feature — just turn **bold**
// segments into real <strong> tags. No dangerouslySetInnerHTML, so AI text
// can never inject HTML; everything else (line breaks, numbering, bullets)
// keeps rendering exactly as it does today via the bubble's white-space: pre-wrap.
const renderMessageText = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const normalizeConversationMessages = (data) => {
  const result = unwrap(data);
  const rawMessages =
    result?.Messages ||
    result?.messages ||
    result?.ChatMessages ||
    result?.chatMessages ||
    result?.Items ||
    result?.items ||
    [];

  if (!Array.isArray(rawMessages)) return [];

  return rawMessages
    .map((msg, index) => {
      const role = String(msg?.Role || msg?.role || msg?.Sender || msg?.sender || "").toLowerCase();
      const text =
        msg?.Message ||
        msg?.message ||
        msg?.Content ||
        msg?.content ||
        msg?.Text ||
        msg?.text ||
        "";

      if (!text) return null;

      const isAssistant =
        role.includes("assistant") ||
        role.includes("ai") ||
        role.includes("bot") ||
        msg?.IsAssistant === true ||
        msg?.isAssistant === true;

      return {
        id: msg?.Id || msg?.id || `history-${index}`,
        role: isAssistant ? "assistant" : "user",
        text,
        assistantMessageId: isAssistant ? msg?.Id || msg?.id : null,
        visualGuide: isAssistant ? normalizeVisualGuide(msg?.VisualGuide ?? msg?.visualGuide) : null,
      };
    })
    .filter(Boolean);
};

const PSAAIAssistant = () => {
  const { t } = useTranslation(["ai", "common"]);
  const location = useLocation();
  const messageEndRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [launcherHovered, setLauncherHovered] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [languageCode, setLanguageCode] = useState(getInitialLanguage);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState(null);

  const pageContext = useMemo(
    () => getContextFromPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!open) return;

    setLoadingSuggestions(true);
    getAiSuggestedQuestions(languageCode)
      .then((res) => {
        const data = unwrap(res.data);
        const list = Array.isArray(data)
          ? data
          : data?.Questions || data?.questions || data?.Items || data?.items || [];
        setSuggestions(
          list
            .map(normalizeSuggestion)
            .filter(Boolean)
            .slice(0, MAX_SUGGESTIONS)
        );
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, [open, languageCode]);

  useEffect(() => {
    if (!open) return;
    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending, open]);

  const refreshConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await getAiConversations();
      const data = unwrap(res.data);
      const list = Array.isArray(data)
        ? data
        : data?.Items || data?.items || data?.Conversations || data?.conversations || [];
      setConversations(list);
    } catch {
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && conversations.length === 0) {
      refreshConversations();
    }
  };

  const loadConversation = async (item) => {
    const id = getConversationId(item);
    if (!id) return;

    setLoadingConversationId(id);
    setError("");
    try {
      const res = await getAiConversation(id);
      const loadedMessages = normalizeConversationMessages(res.data);
      setConversationId(id);
      setMessages(loadedMessages);
      setHistoryOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, t("errors.openConversationFailed")));
    } finally {
      setLoadingConversationId(null);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setFeedbackByMessageId({});
    setError("");
  };

  const submitMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setSending(true);
    setError("");

    try {
      const res = await sendAiChatMessage({
        message: trimmed,
        conversationId,
        screenName: pageContext.screenName,
        eventId: pageContext.eventId,
        languageCode,
      });
      const data = unwrap(res.data);

      if (data?.success === false || data?.Success === false) {
        throw new Error(data?.error || data?.Error || data?.message || data?.Message || t("errors.couldNotAnswer"));
      }

      const nextConversationId =
        data?.conversationId ?? data?.ConversationId ?? conversationId;
      const assistantMessageId =
        data?.assistantMessageId ?? data?.AssistantMessageId ?? null;
      const answer =
        data?.message ||
        data?.Message ||
        data?.answer ||
        data?.Answer ||
        t("errors.noAnswerFound");

      if (nextConversationId) setConversationId(nextConversationId);

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId || `assistant-${Date.now()}`,
          role: "assistant",
          text: answer,
          assistantMessageId,
          visualGuide: normalizeVisualGuide(data?.VisualGuide ?? data?.visualGuide),
        },
      ]);
    } catch (err) {
      const message = getErrorMessage(err, t("errors.unavailable"));
      setError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          text: message,
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitFeedback = async (assistantMessageId, rating) => {
    if (!assistantMessageId || feedbackByMessageId[assistantMessageId]) return;

    try {
      await sendAiFeedback({
        messageId: assistantMessageId,
        rating,
        comment: null,
      });
      setFeedbackByMessageId((prev) => ({
        ...prev,
        [assistantMessageId]: rating,
      }));
      toast.success(t("feedback.sent"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("errors.feedbackFailed")));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitMessage(draft);
  };

  return (
    <>
      {!open && (
        <div style={styles.launcherWrap}>
          <div
            role="tooltip"
            aria-hidden="true"
            style={{
              ...styles.launcherTooltip,
              opacity: launcherHovered ? 1 : 0,
              transform: `translateY(-50%) translateX(${launcherHovered ? 0 : 6}px)`,
            }}
          >
            {t("launcher.hoverTooltip")}
            <span style={styles.launcherTooltipArrow} />
          </div>
          <button
            type="button"
            style={styles.launcher}
            onClick={() => setOpen(true)}
            onMouseEnter={() => setLauncherHovered(true)}
            onMouseLeave={() => setLauncherHovered(false)}
            onFocus={() => setLauncherHovered(true)}
            onBlur={() => setLauncherHovered(false)}
            aria-label={t("launcher.openLabel")}
          >
            {/* No visible text label — just the robot, same as the public site and the
                mobile app's floating assistant button. aria-label above still gives
                screen readers a name for the button. */}
            <RobotAvatar size={64} style={styles.launcherIcon} />
          </button>
        </div>
      )}

      {open && (
        <div style={styles.overlay} onClick={() => setOpen(false)}>
          <section
            style={styles.panel}
            onClick={(event) => event.stopPropagation()}
            aria-label={t("title")}
          >
            <header style={styles.header}>
              <RobotAvatar size={36} iconSize={16} style={styles.headerAvatar} />
              <div style={styles.headerText}>
                <h2 style={styles.title}>{t("title")}</h2>
                <p style={styles.context}>
                  {pageContext.screenName}
                  {pageContext.eventId ? t("header.eventSuffix", { id: pageContext.eventId }) : ""}
                </p>
              </div>
              <div style={styles.headerActions}>
                <select
                  value={languageCode}
                  onChange={(event) => setLanguageCode(normalizeLanguage(event.target.value))}
                  style={styles.languageSelect}
                  aria-label={t("header.languageSelectLabel")}
                >
                  <option value="en">EN</option>
                  <option value="de">DE</option>
                </select>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={toggleHistory}
                  title={t("header.historyTitle")}
                >
                  <History size={15} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={startNewConversation}
                  title={t("header.newConversationTitle")}
                >
                  <Plus size={15} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={() => setOpen(false)}
                  title={t("common:actions.close")}
                >
                  <X size={15} strokeWidth={2.25} />
                </button>
              </div>
            </header>

            {historyOpen && (
              <div style={styles.history}>
                <div style={styles.historyHeader}>
                  <span style={styles.historyTitle}>{t("history.recentConversations")}</span>
                  <button type="button" style={styles.linkButton} onClick={refreshConversations}>
                    {t("history.refresh")}
                  </button>
                </div>
                {loadingConversations ? (
                  <p style={styles.muted}>{t("history.loading")}</p>
                ) : conversations.length === 0 ? (
                  <p style={styles.muted}>{t("history.empty")}</p>
                ) : (
                  <div style={styles.historyList}>
                    {conversations.slice(0, 8).map((item) => {
                      const id = getConversationId(item);
                      return (
                        <button
                          key={id || getConversationTitle(item, t)}
                          type="button"
                          style={styles.historyItem}
                          onClick={() => loadConversation(item)}
                          disabled={loadingConversationId === id}
                        >
                          {loadingConversationId === id ? t("history.opening") : getConversationTitle(item, t)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {error && <div style={styles.errorBanner}>{error}</div>}

            <div style={styles.messages}>
              {messages.length === 0 && !sending ? (
                <div style={styles.emptyState}>
                  <h3 style={styles.emptyTitle}>{t("empty.title")}</h3>
                  <p style={styles.emptyText}>
                    {t("empty.text")}
                  </p>
                  <div style={styles.suggestions}>
                    {loadingSuggestions ? (
                      <span style={styles.muted}>{t("empty.loadingSuggestions")}</span>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((question) => (
                        <button
                          key={question}
                          type="button"
                          style={styles.suggestionChip}
                          onClick={() => submitMessage(question)}
                        >
                          {question}
                        </button>
                      ))
                    ) : (
                      <>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage(t("empty.suggestions.whatIsEqr"))}>
                          {t("empty.suggestions.whatIsEqr")}
                        </button>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage(t("empty.suggestions.whatIsPqr"))}>
                          {t("empty.suggestions.whatIsPqr")}
                        </button>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage(t("empty.suggestions.explainFamilyEvent"))}>
                          {t("empty.suggestions.explainFamilyEvent")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    style={{
                      ...styles.messageRow,
                      ...(message.role === "user" ? styles.userRow : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.bubble,
                        ...(message.role === "user" ? styles.userBubble : styles.assistantBubble),
                        ...(message.isError ? styles.errorBubble : {}),
                      }}
                    >
                      {renderMessageText(message.visualGuide?.summary || message.text)}
                      {message.role === "assistant" && message.visualGuide && (
                        <AIVisualGuide key={`${message.id}-${message.visualGuide.id}-${message.visualGuide.version}`} guide={message.visualGuide} />
                      )}
                    </div>
                    {message.role === "assistant" && message.assistantMessageId && (
                      <div style={styles.feedback}>
                        <button
                          type="button"
                          style={{
                            ...styles.feedbackButton,
                            ...(feedbackByMessageId[message.assistantMessageId] === 1 ? styles.feedbackActive : {}),
                          }}
                          onClick={() => submitFeedback(message.assistantMessageId, 1)}
                          title={t("feedback.helpful")}
                        >
                          <ThumbsUp size={12} strokeWidth={2.25} />
                        </button>
                        <button
                          type="button"
                          style={{
                            ...styles.feedbackButton,
                            ...(feedbackByMessageId[message.assistantMessageId] === -1 ? styles.feedbackActive : {}),
                          }}
                          onClick={() => submitFeedback(message.assistantMessageId, -1)}
                          title={t("feedback.notHelpful")}
                        >
                          <ThumbsDown size={12} strokeWidth={2.25} />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
              {sending && (
                <div style={styles.messageRow}>
                  <div style={{ ...styles.bubble, ...styles.assistantBubble, ...styles.loadingBubble }}>
                    {t("thinking")}
                  </div>
                </div>
              )}
              <div ref={messageEndRef} />
            </div>

            <form style={styles.inputBar} onSubmit={handleSubmit}>
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("inputPlaceholder")}
                style={styles.input}
                rows={1}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitMessage(draft);
                  }
                }}
              />
              <button
                type="submit"
                style={styles.sendButton}
                disabled={sending || !draft.trim()}
                title={t("send")}
              >
                <Send size={15} strokeWidth={2.25} />
              </button>
            </form>
          </section>
        </div>
      )}
    </>
  );
};

const styles = {
  launcherWrap: {
    position: "fixed",
    right: 24,
    bottom: 24,
    zIndex: 1040,
    display: "flex",
    alignItems: "center",
  },
  launcher: {
    position: "relative",
    width: 64,
    height: 64,
    padding: 0,
    border: "none",
    background: "transparent",
    boxShadow: "none",
    cursor: "pointer",
    transition: "transform 0.15s ease",
  },
  launcherTooltip: {
    position: "absolute",
    right: "calc(100% + 14px)",
    top: "50%",
    background: "#1e1e2d",
    color: "#fff",
    padding: "9px 14px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    boxShadow: "0 8px 20px rgba(17,24,39,0.28)",
    pointerEvents: "none",
    transition: "opacity 0.15s ease, transform 0.15s ease",
  },
  launcherTooltipArrow: {
    position: "absolute",
    top: "50%",
    right: -4,
    width: 9,
    height: 9,
    background: "#1e1e2d",
    transform: "translateY(-50%) rotate(45deg)",
    borderRadius: 2,
  },
  // No circle, no background plate — the mascot floats directly on the page, same
  // as the mobile app and the public site's launcher. Drop-shadow keeps it
  // readable against any background color.
  launcherIcon: {
    width: "100%",
    height: "100%",
    color: "#6c63ff",
    filter: "drop-shadow(0 8px 14px rgba(31,41,55,0.35))",
  },
  headerAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#f4f2ff",
    overflow: "hidden",
    color: "#6c63ff",
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1150,
    background: "rgba(17,24,39,0.34)",
    display: "flex",
    justifyContent: "flex-end",
  },
  panel: {
    width: "min(440px, 100vw)",
    height: "100vh",
    background: "#fff",
    boxShadow: "-10px 0 30px rgba(17,24,39,0.22)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid #eef0f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    margin: 0,
    fontSize: 17,
    lineHeight: 1.25,
    color: "#1e1e2d",
  },
  context: {
    margin: "4px 0 0",
    color: "#888",
    fontSize: 12,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  languageSelect: {
    height: 32,
    border: "1px solid #d8dbe3",
    borderRadius: 8,
    color: "#1e1e2d",
    background: "#fff",
    fontSize: 12,
    fontWeight: 700,
    padding: "0 6px",
  },
  iconButton: {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #d8dbe3",
    borderRadius: 8,
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
  },
  history: {
    borderBottom: "1px solid #eef0f4",
    padding: "12px 16px",
    background: "#fafbff",
  },
  historyHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  historyTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#1e1e2d",
  },
  linkButton: {
    border: "none",
    background: "transparent",
    color: "#6c63ff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  historyItem: {
    width: "100%",
    textAlign: "left",
    border: "1px solid #eceef5",
    background: "#fff",
    borderRadius: 8,
    padding: "8px 10px",
    color: "#374151",
    cursor: "pointer",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  errorBanner: {
    margin: "12px 16px 0",
    padding: "9px 11px",
    borderRadius: 8,
    background: "#fff5f5",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontSize: 12,
    lineHeight: 1.45,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: "#f8f9fb",
  },
  emptyState: {
    background: "#fff",
    border: "1px solid #eef0f4",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  emptyTitle: {
    margin: "0 0 6px",
    fontSize: 16,
    color: "#1e1e2d",
  },
  emptyText: {
    margin: "0 0 14px",
    color: "#666",
    fontSize: 13,
    lineHeight: 1.5,
  },
  suggestions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  suggestionChip: {
    border: "1px solid #d8d4ff",
    background: "#f4f2ff",
    color: "#5b54d6",
    borderRadius: 999,
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  muted: {
    margin: 0,
    color: "#888",
    fontSize: 12,
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 5,
  },
  userRow: {
    alignItems: "flex-end",
  },
  bubble: {
    maxWidth: "84%",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userBubble: {
    background: "#6c63ff",
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  assistantBubble: {
    background: "#fff",
    color: "#333",
    border: "1px solid #eef0f4",
    borderBottomLeftRadius: 4,
  },
  errorBubble: {
    background: "#fff5f5",
    borderColor: "#fecaca",
    color: "#b91c1c",
  },
  loadingBubble: {
    color: "#888",
    fontStyle: "italic",
  },
  feedback: {
    display: "flex",
    gap: 6,
    marginLeft: 4,
  },
  feedbackButton: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #d8dbe3",
    background: "#fff",
    color: "#6b7280",
    borderRadius: 999,
    padding: "5px 9px",
    cursor: "pointer",
  },
  feedbackActive: {
    borderColor: "#6c63ff",
    color: "#6c63ff",
    background: "#f4f2ff",
  },
  inputBar: {
    padding: 14,
    borderTop: "1px solid #eef0f4",
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    background: "#fff",
  },
  input: {
    flex: 1,
    minHeight: 42,
    maxHeight: 110,
    resize: "vertical",
    border: "1px solid #d8dbe3",
    borderRadius: 10,
    padding: "11px 12px",
    fontFamily: "inherit",
    fontSize: 13,
    lineHeight: 1.35,
    outline: "none",
  },
  sendButton: {
    height: 42,
    width: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: 10,
    background: "#6c63ff",
    color: "#fff",
    cursor: "pointer",
    flexShrink: 0,
  },
};

export default PSAAIAssistant;
